import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useTranslation } from 'react-i18next';

const { FiCamera, FiUsers, FiCheckCircle, FiDollarSign, FiStar, FiClock, FiTrendingUp, FiBarChart3, FiHome, FiMessageCircle, FiPieChart, FiShield, FiTarget, FiAward, FiCalendar, FiGlobe, FiSettings, FiTool, FiHeadphones, FiMonitor } = FiIcons;

const Services = () => {
  const { t } = useTranslation();

  const mainServices = [
    {
      icon: FiHome,
      displayIcon: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80",
      title: t('Promozione & Marketing Digitale'),
      description: t('Gestiamo completamente la presenza online del tuo immobile su tutte le principali piattaforme di prenotazione per massimizzare visibilità e prenotazioni.'),
      features: [
        t('Gestione annunci su Airbnb,Booking.com e altre 15+ piattaforme'),
        t('Shooting fotografico professionale con droni e virtual tour 360°'),
        t('Copywriting ottimizzato SEO per aumentare le conversioni'),
        t('Gestione calendario sincronizzato in tempo reale'),
        t('Ottimizzazione posizionamento nei risultati di ricerca'),
        t('A/B testing continuo di titoli e descrizioni')
      ],
      benefits: [
        t('Visibilità su 20+ piattaforme internazionali'),
        t('Aumento medio del 60% delle visualizzazioni'),
        t('Tasso di conversione superiore del 35%'),
        t('Posizionamento nei primi 3 risultati')
      ]
    },
    {
      icon: FiUsers,
      displayIcon: "https://images.unsplash.com/photo-1515378960530-7c0da6231fb1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80",
      title: t('Guest Experience Management'),
      description: t('Gestione completa dell\'esperienza degli ospiti dall\'inquiery alla partenza,garantendo recensioni eccellenti e ospiti fidelizzati.'),
      features: [
        t('Comunicazione multilingue pre-arrivo (IT,EN,FR,DE,ES)'),
        t('Check-in automatico con smart lock e codici personalizzati'),
        t('Assistenza ospiti H24/7 via WhatsApp,telefono e chat'),
        t('Gestione richieste speciali e servizi aggiuntivi'),
        t('Protocollo di gestione reclami e problem solving'),
        t('Follow-up post-soggiorno per recensioni positive')
      ],
      benefits: [
        t('Rating medio 4.8/5 su tutte le piattaforme'),
        t('95% di recensioni positive'),
        t('Tempo di risposta medio: 15 minuti'),
        t('Tasso di fidelizzazione ospiti del 40%')
      ]
    },
    {
      icon: FiCheckCircle,
      displayIcon: "https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753355242615-Impresa-di-pulizie-a-Rho-1170x780-203733767.jpg",
      title: t('Housekeeping & Maintenance'),
      description: t('Standard professionali di pulizia e manutenzione per garantire immobili sempre perfetti e ospiti soddisfatti.'),
      features: [
        t('Pulizie professionali certificate tra ogni soggiorno'),
        t('Controllo qualità con checklist a 47 punti'),
        t('Rifornimento completo amenities e prodotti di cortesia'),
        t('Manutenzione ordinaria e straordinaria coordinata'),
        t('Rete di fornitori qualificati (idraulici,elettricisti,etc.)'),
        t('Ispezioni periodiche e report fotografici')
      ],
      benefits: [
        t('Standard 5 stelle certificati'),
        t('Tempo medio pulizia: 2.5 ore'),
        t('Zero reclami per pulizia negli ultimi 12 mesi'),
        t('Risparmio del 30% sui costi di manutenzione')
      ]
    },
    {
      icon: FiBarChart3,
      displayIcon: "https://images.unsplash.com/photo-1543286386-713bdd548da4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80",
      title: t('Revenue Management Avanzato'),
      description: t('Strategie di pricing dinamico e analisi di mercato per massimizzare i ricavi in ogni stagione e condizione di mercato.'),
      features: [
        t('Algoritmi di pricing dinamico basati su 50+ variabili'),
        t('Analisi competitor e benchmarking continuo'),
        t('Previsioni stagionali e ottimizzazione calendario'),
        t('Report mensili dettagliati con KPI e raccomandazioni'),
        t('Monitoraggio ROI e performance multi-piattaforma'),
        t('Strategie di upselling e servizi aggiuntivi')
      ],
      benefits: [
        t('Aumento medio ricavi del 45%'),
        t('Ottimizzazione occupancy rate al 78%'),
        t('ADR superiore del 25% alla media locale'),
        t('ROI tracking mensile dettagliato')
      ]
    }
  ];

  const additionalServices = [
    {
      icon: FiShield,
      title: t('Gestione Legale & Fiscale'),
      description: t('Supporto completo per adempimenti normativi,CIR/CIN e ottimizzazione fiscale.'),
      features: [t('Gestione CIR e CIN regionali'), t('Consulenza fiscale specializzata'), t('Contratti e assicurazioni'), t('Compliance normative locali')]
    },
    {
      icon: FiSettings,
      title: t('Smart Home Integration'),
      description: t('Installazione e gestione tecnologie smart per automatizzare check-in e controlli.'),
      features: [t('Smart lock professionali'), t('Sistemi di monitoraggio'), t('Automazione luci e clima'), t('App mobile dedicata')]
    },
    {
      icon: FiGlobe,
      title: t('Marketing Internazionale'),
      description: t('Promozione mirata per mercati internazionali e segmenti specifici di viaggiatori.'),
      features: [t('Campagne Google Ads'), t('Social media marketing'), t('Partnerships tour operator'), t('SEO multilingue')]
    },
    {
      icon: FiTool,
      title: t('Property Enhancement'),
      description: t('Consulenza per miglioramenti strutturali e arredamento per aumentare l\'appeal.'),
      features: [t('Audit immobile completo'), t('Consulenza interior design'), t('Coordinamento lavori'), t('ROI improvement progetti')]
    }
  ];

  const packages = [
    {
      name: 'Essential',
      description: t('Perfetto per iniziare'),
      price: '15%',
      priceNote: t('delle entrate lorde'),
      features: [
        t('Gestione base multi-piattaforma'),
        t('Comunicazione ospiti standard'),
        t('Pulizie professionali'),
        t('Report mensili'),
        t('Assistenza telefonica')
      ],
      highlighted: false
    },
    {
      name: 'Professional',
      description: t('La scelta più popolare'),
      price: '20%',
      priceNote: t('delle entrate lorde'),
      features: [
        t('Tutto di Essential +'),
        t('Revenue management avanzato'),
        t('Assistenza H24/7'),
        t('Fotografia professionale'),
        t('Manutenzione coordinata'),
        t('Guest experience premium')
      ],
      highlighted: true
    },
    {
      name: 'Premium',
      description: t('Massima performance'),
      price: '25%',
      priceNote: t('delle entrate lorde'),
      features: [
        t('Tutto di Professional +'),
        t('Smart home integration'),
        t('Marketing internazionale'),
        t('Consulenza property enhancement'),
        t('Account manager dedicato'),
        t('Garanzia risultati')
      ],
      highlighted: false
    }
  ];

  const process = [
    {
      step: '01',
      title: t('Analisi & Valutazione'),
      description: t('Sopralluogo gratuito,analisi mercato locale e stima potenziale ricavi'),
      duration: t('1-2 giorni'),
      icon: FiTarget
    },
    {
      step: '02',
      title: t('Setup & Ottimizzazione'),
      description: t('Fotografie professionali,creazione annunci e setup tecnologico'),
      duration: t('3-5 giorni'),
      icon: FiCamera
    },
    {
      step: '03',
      title: t('Lancio Multi-Piattaforma'),
      description: t('Pubblicazione su tutte le OTA e inizio gestione operativa'),
      duration: t('2-3 giorni'),
      icon: FiGlobe
    },
    {
      step: '04',
      title: t('Gestione & Ottimizzazione'),
      description: t('Gestione quotidiana,monitoraggio performance e ottimizzazioni continue'),
      duration: t('Ongoing'),
      icon: FiTrendingUp
    }
  ];

  const guarantees = [
    {
      icon: FiAward,
      title: t('Garanzia Risultati 90 giorni'),
      description: t('Se non aumentiamo i tuoi ricavi del 25% nei primi 90 giorni,rimborsiamo tutte le commissioni del primo mese')
    },
    {
      icon: FiClock,
      title: t('Attivazione Rapida'),
      description: t('Il tuo immobile sarà online e prenotabile entro 7 giorni lavorativi dalla firma del contratto')
    },
    {
      icon: FiShield,
      title: t('Copertura Assicurativa'),
      description: t('Ogni prenotazione è coperta da assicurazione danni fino a €50.000 e responsabilità civile')
    },
    {
      icon: FiHeadphones,
      title: t('Supporto Dedicato'),
      description: t('Account manager dedicato e supporto tecnico H24/7 per emergenze e assistenza immediata')
    }
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-warm-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6 }}
              className="mb-6"
            >
              <span className="bg-primary-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                {t('Servizi Professionali Certificati')}
              </span>
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl md:text-6xl font-bold text-warm-900 mb-6"
            >
              {t('Servizi Completi per')} <span className="block text-primary-600">{t('Affitti Brevi di Successo')}</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-warm-700 mb-8 max-w-4xl mx-auto leading-relaxed"
            >
              {t('Gestione professionale chiavi in mano che trasforma il tuo immobile in una fonte di reddito ottimale. Dalla promozione alla manutenzione,ci occupiamo di tutto con standard da 5 stelle.')}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Property Showcase */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6 }} 
            viewport={{ once: true }}
            className="rounded-2xl overflow-hidden shadow-2xl"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
              <img 
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753287373694-blob" 
                alt="Villa Monterosso - Cinque Terre" 
                className="w-full h-64 object-cover" 
              />
              <img 
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753287967247-oleandri02-461242530.jpg" 
                alt="Residence Gli Oleandri" 
                className="w-full h-64 object-cover" 
              />
              <img 
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753288194892-Loft-in-vendita-milano-608858958.jpg" 
                alt="Loft Milano Centro" 
                className="w-full h-64 object-cover" 
              />
            </div>
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white p-6 text-center">
              <h3 className="text-xl font-bold mb-2">150+ {t('Immobili in Gestione Attiva')}</h3>
              <p className="text-primary-100">{t('Rating medio 4.9/5 • +45% ricavi medi • 98% clienti soddisfatti')}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-warm-900 mb-4">{t('I Nostri Servizi Core')}</h2>
            <p className="text-xl text-warm-600 max-w-3xl mx-auto">{t('Quattro pilastri fondamentali per il successo del tuo affitto breve')}</p>
          </div>
          <div className="space-y-24">
            {mainServices.map((service, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, y: 40 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: index * 0.1 }} 
                viewport={{ once: true }}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12`}
              >
                <div className="flex-1">
                  <div className="flex items-center mb-6">
                    <div className="bg-primary-600 w-16 h-16 rounded-2xl flex items-center justify-center mr-4">
                      <SafeIcon icon={service.icon} className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <span className="text-primary-600 font-semibold text-sm">{t('SERVIZIO')} {index + 1}</span>
                      <h3 className="text-3xl font-bold text-warm-900">{service.title}</h3>
                    </div>
                  </div>
                  <p className="text-lg text-warm-600 mb-6 leading-relaxed">{service.description}</p>
                  <div className="mb-8">
                    <h4 className="font-semibold text-warm-900 mb-4">{t('Cosa Include:')}</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {service.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-start space-x-3">
                          <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0" />
                          <span className="text-warm-700 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-primary-50 rounded-xl p-6">
                    <h4 className="font-semibold text-warm-900 mb-4 flex items-center">
                      <SafeIcon icon={FiTrendingUp} className="h-5 w-5 mr-2 text-primary-600" />
                      {t('Risultati Garantiti:')}
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {service.benefits.map((benefit, benefitIndex) => (
                        <div key={benefitIndex} className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                          <span className="text-warm-700 text-sm font-medium">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex-1 max-w-lg">
                  <div className="bg-warm-50 rounded-2xl overflow-hidden shadow-xl">
                    <img 
                      src={service.displayIcon} 
                      alt={service.title} 
                      className="w-full h-80 object-cover" 
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-warm-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-warm-900 mb-4">{t('Servizi Aggiuntivi Specializzati')}</h2>
            <p className="text-xl text-warm-600 max-w-2xl mx-auto">{t('Soluzioni avanzate per massimizzare il potenziale del tuo immobile')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {additionalServices.map((service, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: index * 0.1 }} 
                viewport={{ once: true }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-200"
              >
                <div className="bg-primary-600 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                  <SafeIcon icon={service.icon} className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-warm-900 mb-4">{service.title}</h3>
                <p className="text-warm-600 mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center space-x-3">
                      <SafeIcon icon={FiCheckCircle} className="h-4 w-4 text-primary-600" />
                      <span className="text-warm-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-warm-900 mb-4">{t('Il Nostro Processo in 4 Step')}</h2>
            <p className="text-xl text-warm-600 max-w-2xl mx-auto">{t('Dalla valutazione iniziale al successo operativo in meno di 2 settimane')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: index * 0.1 }} 
                viewport={{ once: true }}
                className="text-center group"
              >
                <div className="relative mb-8">
                  <div className="bg-primary-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <SafeIcon icon={step.icon} className="h-10 w-10 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 bg-warm-900 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">
                    {step.step}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-warm-900 mb-3">{step.title}</h3>
                <p className="text-warm-600 mb-3 text-sm leading-relaxed">{step.description}</p>
                <div className="bg-primary-50 text-primary-700 px-3 py-1 rounded-full text-xs font-medium inline-block">
                  {step.duration}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-20 bg-warm-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-warm-900 mb-4">{t('Pacchetti di Servizio')}</h2>
            <p className="text-xl text-warm-600 max-w-2xl mx-auto">{t('Scegli il livello di servizio più adatto alle tue esigenze')}</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: index * 0.1 }} 
                viewport={{ once: true }}
                className={`bg-white rounded-2xl shadow-lg p-8 relative ${pkg.highlighted ? 'ring-4 ring-primary-600 scale-105 z-10' : ''}`}
              >
                {pkg.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                      {t('PIÙ POPOLARE')}
                    </span>
                  </div>
                )}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-warm-900 mb-2">{pkg.name}</h3>
                  <p className="text-warm-600 mb-4">{pkg.description}</p>
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-primary-600">{pkg.price}</span>
                    <div className="text-sm text-warm-600">{pkg.priceNote}</div>
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center space-x-3">
                      <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-primary-600 flex-shrink-0" />
                      <span className="text-warm-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button className={`w-full py-4 rounded-xl font-semibold transition-colors ${pkg.highlighted ? 'bg-primary-600 text-white hover:bg-primary-700' : 'bg-primary-50 text-primary-600 hover:bg-primary-100'}`}>
                  {t('Scegli')} {pkg.name}
                </button>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <p className="text-warm-600 mb-4">{t('Non sei sicuro di quale pacchetto scegliere?')}</p>
            <a 
              href="mailto:elviraviglianopm@gmail.com" 
              className="inline-block bg-primary-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-primary-700 transition-colors"
            >
              {t('Richiedi una Consulenza Personalizzata')}
            </a>
          </div>
        </div>
      </section>

      {/* Guarantees Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-warm-900 mb-4">{t('Le Nostre Garanzie')}</h2>
            <p className="text-xl text-warm-600 max-w-2xl mx-auto">{t('Investiamo nel tuo successo con garanzie concrete e misurabili')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {guarantees.map((guarantee, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: index * 0.1 }} 
                viewport={{ once: true }}
                className="text-center group"
              >
                <div className="bg-gradient-to-br from-primary-500 to-primary-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <SafeIcon icon={guarantee.icon} className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-warm-900 mb-4">{guarantee.title}</h3>
                <p className="text-warm-600 leading-relaxed">{guarantee.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-primary-600 to-primary-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6 }} 
            viewport={{ once: true }}
          >
            <div className="mb-8">
              <img 
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1752009564982-photo_5999181830990974370_w.jpg" 
                alt="Elvira Vigliano Property Manager" 
                className="h-32 w-32 rounded-full border-4 border-white/30 shadow-lg mx-auto mb-6" 
              />
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">{t('Inizia il Tuo Successo Oggi')}</h2>
              <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto leading-relaxed">
                {t('Unisciti a oltre 150 proprietari che hanno trasformato i loro immobili in fonti di reddito ottimali. Consulenza gratuita,setup rapido,risultati garantiti.')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
              <a 
                href="tel:+393770457022" 
                className="inline-flex items-center justify-center bg-white text-primary-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-primary-50 transition-colors shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <SafeIcon icon={FiPhone} className="mr-3 h-5 w-5" />
                {t('Chiamaci per Iniziare')}
              </a>
              <a 
                href="mailto:elviraviglianopm@gmail.com" 
                className="inline-flex items-center justify-center bg-primary-700 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-primary-800 transition-colors shadow-lg hover:shadow-xl transform hover:scale-105 border-2 border-white/20"
              >
                <SafeIcon icon={FiMail} className="mr-3 h-5 w-5" />
                {t('Richiedi Preventivo')}
              </a>
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-primary-200 text-sm">
              <div className="flex items-center">
                <SafeIcon icon={FiCheckCircle} className="h-4 w-4 mr-2" />
                <span>{t('Consulenza gratuita di 60 minuti')}</span>
              </div>
              <div className="flex items-center">
                <SafeIcon icon={FiCheckCircle} className="h-4 w-4 mr-2" />
                <span>{t('Setup completo in 7 giorni')}</span>
              </div>
              <div className="flex items-center">
                <SafeIcon icon={FiCheckCircle} className="h-4 w-4 mr-2" />
                <span>{t('Garanzia risultati 90 giorni')}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;